import axios from 'axios';
import { API_URL } from '../../config/api';
import { expiresSoon } from './expiry';
export { expiresSoon } from './expiry';

export interface Subscription {
  id: number; user_id: number; username: string | null; email: string | null;
  full_name: string | null; avatar_url: string | null; product_id: string;
  original_transaction_id: string; purchase_date: string | null; expires_date: string | null;
  is_active: boolean; is_trial: boolean; is_valid: boolean; is_in_billing_retry: boolean;
  is_in_grace_period: boolean; days_remaining: number; environment: string | null;
  created_at: string | null; updated_at: string | null;
}
export interface Stats {
  total_subscriptions: number; active_subscriptions: number; expired_subscriptions: number;
  trial_subscriptions: number; premium_users: number; expiring_soon: number;
  by_product: { weekly: number; monthly: number; yearly: number };
  by_tier?: Record<Tier, number>; by_plan?: Record<string, number>;
  estimated_monthly_revenue: number;
}
export type Tier = 'plus' | 'pro' | 'ultra';
export type Period = 'weekly' | 'monthly' | 'yearly';
export type Status = 'all' | 'active' | 'expired' | 'trial' | 'expiring' | 'billing_issue';
export type Sort = 'newest' | 'expiring_soon' | 'days_desc' | 'name';
export type Plan = Tier | 'legacy' | 'gift' | 'other';
export interface Filters { status: Status; plan: '' | Plan; environment: string }
export const defaults: Filters = { status: 'all', plan: '', environment: '' };
export const statusLabels: Record<Status, string> = {
  all: 'Tüm kayıtlar', active: 'Geçerli', expired: 'Pasif / süresi dolan', trial: 'Deneme',
  expiring: '7 gün içinde biten', billing_issue: 'Ödeme sorunu',
};
export const planLabels: Record<Plan, string> = { plus: 'Plus', pro: 'Pro', ultra: 'Ultra', legacy: 'Eski Premium', gift: 'Hediye', other: 'Diğer' };
export const tierLabels: Record<Tier, string> = { plus: 'Plus', pro: 'Pro', ultra: 'Ultra' };
export const periodLabels: Record<Period, string> = { weekly: 'Haftalık', monthly: 'Aylık', yearly: 'Yıllık' };
// Mirrors PRODUCTS in Backend/app/core/subscription_tiers.py. Never classify
// by substring: "com.lifeshare.pro.monthly" is the middle tier, and
// "com.lifeshare.plus.monthly" is not the legacy monthly plan.
const PRODUCTS: Record<string, [Plan, Tier, Period | null]> = {
  'com.lifeshare.plus.monthly': ['plus', 'plus', 'monthly'], 'com.lifeshare.plus.yearly': ['plus', 'plus', 'yearly'],
  'com.lifeshare.pro.monthly': ['pro', 'pro', 'monthly'], 'com.lifeshare.pro.yearly': ['pro', 'pro', 'yearly'],
  'com.lifeshare.ultra.monthly': ['ultra', 'ultra', 'monthly'], 'com.lifeshare.ultra.yearly': ['ultra', 'ultra', 'yearly'],
  'com.lifeshare.weekly': ['legacy', 'pro', 'weekly'], 'com.lifeshare.monthly': ['legacy', 'pro', 'monthly'], 'com.lifeshare.yearly': ['legacy', 'pro', 'yearly'],
  'com.lifeshare.admin.grant': ['gift', 'ultra', null], 'com.lifeshare.admin.grant.plus': ['gift', 'plus', null], 'com.lifeshare.admin.grant.pro': ['gift', 'pro', null],
};
// Ids missing from the map (old Android/unverified rows, custom admin ids):
// the server treats them as legacy Premium (Pro) and guesses the period
// from the id, so the panel does the same.
function product(id: string): [Plan, Tier, Period | null] {
  if (PRODUCTS[id]) return PRODUCTS[id];
  const period: Period | null = id.includes('yearly') ? 'yearly' : id.includes('weekly') ? 'weekly' : id.includes('monthly') ? 'monthly' : null;
  return [id.includes('admin') ? 'gift' : period ? 'legacy' : 'other', 'pro', period];
}
export const planKey = (id: string): Plan => product(id)[0];
export const tierOf = (id: string): Tier => product(id)[1];
export function planText(id: string) {
  const [plan, tier, period] = product(id);
  if (plan === 'gift') return `Hediye · ${tierLabels[tier]}`;
  return period ? `${planLabels[plan]} · ${periodLabels[period]}` : planLabels[plan];
}
export const sortLabels: Record<Sort, string> = { newest: 'En yeni kayıt', expiring_soon: 'Kalan gün · azdan çoğa', days_desc: 'Kalan gün · çoktan aza', name: 'İsim · A–Z' };
export const isGift = (item: Subscription) => item.product_id.includes('admin');
export const nameOf = (item: Subscription) => item.full_name || item.username || `Kullanıcı #${item.user_id}`;
export const environmentLabel = (value: string | null) => value?.toLowerCase() === 'production' ? 'Canlı' : value?.toLowerCase() === 'sandbox' ? 'Test ortamı' : value || 'Bilinmiyor';
export const stateLabel = (item: Subscription) => item.is_valid ? 'Geçerli' : item.is_active ? 'Süresi doldu' : 'Pasif';
export const number = (value: number) => value.toLocaleString('tr-TR');
export const money = (value: number) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);
export function matches(item: Subscription, query: string, filter: Filters) {
  const text = [item.id, item.user_id, item.username, item.full_name, item.email, item.product_id, item.original_transaction_id].join(' ').toLocaleLowerCase('tr-TR');
  const status = filter.status;
  return text.includes(query.trim().toLocaleLowerCase('tr-TR')) &&
    (status === 'all' || (status === 'active' && item.is_valid) || (status === 'expired' && !item.is_valid) ||
      (status === 'trial' && item.is_trial) || (status === 'expiring' && expiresSoon(item)) ||
      (status === 'billing_issue' && (item.is_in_billing_retry || item.is_in_grace_period))) &&
    (!filter.plan || planKey(item.product_id) === filter.plan) && (!filter.environment || item.environment === filter.environment);
}
export function sortItems(items: Subscription[], sort: Sort) {
  return [...items].sort((a, b) => sort === 'name' ? nameOf(a).localeCompare(nameOf(b), 'tr') :
    sort === 'days_desc' ? b.days_remaining - a.days_remaining : sort === 'expiring_soon' ? a.days_remaining - b.days_remaining :
      new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
}
export async function fetchSubscriptions() {
  const all = new Map<number, Subscription>();
  for (let page = 0; page < 100; page += 1) {
    const { data } = await axios.get<{ total: number; subscriptions: Subscription[] }>(`${API_URL}/admin/subscriptions`, { params: { skip: page * 1000, limit: 1000 } });
    if (!Array.isArray(data.subscriptions)) throw new Error('Invalid subscription response');
    data.subscriptions.forEach(item => all.set(item.id, item));
    const total = Number.isFinite(data.total) ? data.total : all.size;
    if (data.subscriptions.length < 1000 || (Number.isFinite(data.total) && all.size >= total)) return { items: Array.from(all.values()), total, partial: false };
    if (page === 99) return { items: Array.from(all.values()), total, partial: true };
  }
  throw new Error('Subscription pagination did not terminate');
}
export function requestError(error: unknown) {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  return status === 403 ? 'Bu işlem için yetkiniz yok.' : status === 404 ? 'İlgili kayıt bulunamadı.' : status === 422 ? 'Girilen bilgileri kontrol edin.' :
    status === 429 ? 'Çok fazla istek gönderildi. Biraz sonra yeniden deneyin.' : 'İşlem tamamlanamadı. Yeniden deneyin.';
}
